import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { listConnectors } from "../../../api/connectorsApi";
import type { ThreadOverview } from "../../../api/threads/threadDecoders";
import { approveHostRequest, declineHostOffer, readAutomaticHost } from "../../../api/threads/threadHostingApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { refusalText } from "../../../ui/feedback/refusalWords";
import ConnectorSetupButton from "../../connectors/setup/ConnectorSetupButton";
import { isAnotherPc, readThisPcMark } from "../../connectors/thisPc/thisPc";
import { notifyThreadListChanged } from "../data/threadListEvents";
import { useThreadContext } from "../page/threadPageContext";
import { actionDecision, actionOffer, allows } from "../page/viewerActions";
import AttachConnectorDialog from "./AttachConnectorDialog";
import { runOnOwnConnector } from "./automaticHosting";
import HostingRow from "./HostingRow";
import { hostOfferDecided, openHostOffers, useHostOfferInbox } from "./incomingHostOffers";
import { onlineConnectors } from "./routeChoices";
import TakeOver from "./TakeOver";

/**
 * Where the Thread runs, in one line (F55): it replaces the "Saved" and "Live" chips and "On
 * your connector". It says only where (F55 (a), 29 Oct): your own Thread "Runs on: ‹PC›" with
 * Change for the PC; not running, "Run on ‹PC›" in one click, or the hosting form when there is
 * a choice to make. What it runs with is the "Runs with" row's (W-07);
 * with no connector of yours online, Set up my PC; a request for your role, to approve or
 * decline. Someone else's Thread: `TakeOver`.
 */
export default function HostingLine({ lastEntryNumber }: { lastEntryNumber: number }) {
  const { overview, currentUserId } = useThreadContext();
  // Module C's decisions for this viewer (W-22 slice 4): the host runs it; someone else may ask to.
  if (!allows(overview.viewer, "attach")) {
    return actionOffer(overview.viewer, "request_hosting") === null ? null : <TakeOver overview={overview} lastEntryNumber={lastEntryNumber} />;
  }
  return (
    <>
      <OwnHosting overview={overview} lastEntryNumber={lastEntryNumber} currentUserId={currentUserId} />
      <RequestsForYourRole threadId={overview.threadId} lastEntryNumber={lastEntryNumber} />
    </>
  );
}

/** How often a Thread that doesn't run looks for your connectors again: the setup guide's idle pace. */
const NOT_RUNNING_POLL_MILLISECONDS = 15_000;

function OwnHosting({ overview, lastEntryNumber, currentUserId }: { overview: ThreadOverview; lastEntryNumber: number; currentUserId: string | null }) {
  const { t } = useTranslation();
  const threadId = overview.threadId;
  // The PCs by name, and where the server would run the Thread without asking (F55, BA12-02).
  const load = useCallback((signal: AbortSignal) => Promise.all([listConnectors(signal), readAutomaticHost(threadId, signal)]), [threadId]);
  const { state: loaded, refresh } = useLoad(load);
  // Read again when the Thread goes live or saved, or its LLM changes (Sol R1 S4), so the line
  // names the PC it runs on now and offers where it would run.
  const hosting = `${overview.state}:${overview.attachedConnector ?? ""}:${JSON.stringify(overview.settings.route)}`;
  const shownFor = useRef(hosting);
  useEffect(() => {
    if (shownFor.current !== hosting) {
      shownFor.current = hosting;
      refresh();
    }
  }, [hosting, refresh]);
  // While it doesn't run, a PC connected meanwhile shows up, as in the setup guide's idle check.
  const live = overview.state === "live";
  useEffect(() => {
    if (live) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, NOT_RUNNING_POLL_MILLISECONDS);
    return () => {
      window.clearInterval(timer);
    };
  }, [live, refresh]);
  const [changing, setChanging] = useState(false);
  const { state: running, submit } = useSubmission();
  if (loaded.status !== "loaded") return null;
  const [connectors, host] = loaded.value;
  const pcOf = (connectorId: string | null) => connectors.find((connector) => connector.connectorId === connectorId)?.name ?? t("threads.header.yourPc");
  const dialog = changing ? <AttachConnectorDialog lastEntryNumber={lastEntryNumber} onClose={() => { setChanging(false); }} /> : null;

  if (overview.state === "live") {
    // Plainly when it is another PC than this browser's, as far as the person said which (TA-6).
    const elsewhere = currentUserId !== null && overview.attachedConnector !== null && isAnotherPc(connectors, readThisPcMark(currentUserId), overview.attachedConnector);
    return (
      <HostingRow>
        <span>{t(elsewhere ? "threads.header.runsOnAnotherPc" : "threads.header.runsOn", { place: pcOf(overview.attachedConnector) })}</span>
        <AppButton variant="secondary" size="xs" onClick={() => { setChanging(true); }}>{t("threads.header.change")}</AppButton>
        {dialog}
      </HostingRow>
    );
  }
  if (onlineConnectors(connectors).length === 0) {
    return (
      <HostingRow>
        <span>{t("threads.header.noConnector")}</span>
        <ConnectorSetupButton inline={true} />
      </HostingRow>
    );
  }
  return (
    <HostingRow>
      <span>{t("threads.header.notRunning")}</span>
      {host === null ? null : (
        <AppButton
          variant="primary"
          size="xs"
          disabled={running.status === "submitting"}
          onClick={() => {
            submit(async () => {
              if (!(await runOnOwnConnector(overview.threadId))) setChanging(true);
              notifyThreadListChanged();
            });
          }}
        >
          {t("threads.header.runOn", { place: pcOf(host.connectorId) })}
        </AppButton>
      )}
      <AppButton variant="secondary" size="xs" onClick={() => { setChanging(true); }}>{t(host === null ? "threads.header.chooseWhere" : "threads.header.change")}</AppButton>
      {running.status === "failed" ? <ErrorNotice error={running.error} compact={true} className="w-full text-xs" /> : null}
      {dialog}
    </HostingRow>
  );
}

/**
 * Someone asks for your role in this Thread (F55): it would then run on their PC. One click
 * hands it over (T-9); or decline. The requests come with the inbox (Q-60).
 */
function RequestsForYourRole({ threadId, lastEntryNumber }: { threadId: string; lastEntryNumber: number }) {
  const { t } = useTranslation();
  const { overview } = useThreadContext();
  const requests = openHostOffers(useHostOfferInbox()).filter((offer) => offer.threadId === threadId && offer.state === "requested");
  // Module C's decision (W-22 slice 4): the role changes hands only with no turn open, and says so.
  const approving = actionDecision(overview.viewer, "approve_hosting");
  const { state, submit } = useSubmission();
  const busy = state.status === "submitting";
  const decide = (offerId: string, action: () => Promise<unknown>) => {
    submit(async () => {
      await action();
      hostOfferDecided(offerId);
      notifyThreadListChanged();
    });
  };
  return requests.map((request) => (
    <HostingRow key={request.offerId}>
      <span className="app-text">{t("threads.header.requestFrom", { name: request.toDisplayName })}</span>
      <AppButton
        variant="primary"
        size="xs"
        disabled={busy}
        unavailable={approving.refusal === null ? undefined : refusalText(t, approving.refusal)}
        onClick={() => { decide(request.offerId, () => approveHostRequest(request.offerId, lastEntryNumber)); }}
      >
        {t("threads.header.handOver")}
      </AppButton>
      <AppButton variant="secondary" size="xs" disabled={busy} onClick={() => { decide(request.offerId, () => declineHostOffer(request.offerId)); }}>
        {t("threads.header.decline")}
      </AppButton>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} className="w-full text-xs" /> : null}
    </HostingRow>
  ));
}
